import React, { useEffect, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';

import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import type { Snippet } from '../types/snippet.ts';

import { useSnippetPopupPreview } from './preview/SnippetPopupPreviewContext.tsx';
import { SnippetPanelItem, SNIPPET_ITEM_HEIGHT } from './snippet-panel-item/SnippetPanelItem.tsx';

const styles = cssMap({
	container: {
		flexGrow: 1,
		minHeight: '0px',
		overflow: 'hidden',
	},
});

export const useVirtualizedListHeight = (
	containerRef: React.RefObject<HTMLDivElement | null>,
): { containerHeight: number } => {
	const [containerHeight, setContainerHeight] = useState(0);

	useEffect(() => {
		const node = containerRef.current;
		if (!node) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) {
				setContainerHeight(entry.contentRect.height);
			}
		});

		observer.observe(node);
		return () => observer.disconnect();
	}, [containerRef]);

	return { containerHeight };
};

type SnippetPanelListProps = {
	snippets: Snippet[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelList = ({
	snippets,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelListProps) => {
	const { hidePreview } = useSnippetPopupPreview();

	const containerRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<List>(null);

	const { containerHeight } = useVirtualizedListHeight(containerRef);

	useEffect(() => {
		// Scroll to the top of the list when the snippets change as sort order may not be "most recent"
		listRef.current?.scrollTo(0);
	}, [snippets]);

	const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => (
		<div style={style} role="listitem">
			<SnippetPanelItem
				snippet={snippets[index]}
				contentId={contentId}
				spaceId={spaceId}
				spaceKey={spaceKey}
			/>
		</div>
	);

	return (
		<Box xcss={styles.container} ref={containerRef} onMouseLeave={hidePreview}>
			{containerHeight > 0 && (
				<List
					ref={listRef}
					height={containerHeight}
					itemCount={snippets.length}
					itemSize={SNIPPET_ITEM_HEIGHT}
					width="100%"
					itemKey={(index) => snippets[index].snippetId}
					overscanCount={3}
				>
					{Row}
				</List>
			)}
		</Box>
	);
};
